"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UserMutator = exports.SSOUserService = exports.UserService = void 0;
const tslib_1 = require("tslib");
const core_api_1 = require("@ikegps/core-api");
const core_shared_1 = require("@ikegps/core-shared");
const mobx_1 = require("mobx");
const notification_service_1 = require("../components/notification.toast/notification.service");
const i18n_1 = require("../i18n");
const index_1 = require("./index");
const USER_STORAGE_KEY = 'currentUser';
class UserService {
    constructor() {
        this.__priority = 25;
        this.current = null;
        this.lastDepartmentId = null;
    }
    get hasUser() {
        return this.current !== null;
    }
    isIkeAnalyst(currentJson) {
        return !!(currentJson === null || currentJson === void 0 ? void 0 : currentJson.email.endsWith('ikegps.com'));
    }
    hasFormPermission(formOption) {
        if (formOption === core_api_1.IkeFieldType.FILE_TOOL) {
            return index_1.Services.Token.has(core_api_1.IkeToken.FEATURE_GROUP_IKE_ANALYZE);
        }
        if (formOption === core_api_1.IkeFieldType.VERTICAL_SEPARATION) {
            return (index_1.Services.Token.has(core_api_1.IkeToken.FEATURE_FORM_VERTICAL_SEPARATION) ||
                index_1.Services.Token.has(core_api_1.IkeToken.FEATURE_GROUP_IKE_ANALYZE));
        }
        return true;
    }
    setUser(user) {
        this.current = user;
        if (user.settings != null) {
            this.lastDepartmentId = user.settings.departmentId;
        }
        return this.current;
    }
}
exports.UserService = UserService;
tslib_1.__decorate([
    mobx_1.observable
], UserService.prototype, "current", void 0);
tslib_1.__decorate([
    mobx_1.observable
], UserService.prototype, "lastDepartmentId", void 0);
tslib_1.__decorate([
    mobx_1.computed
], UserService.prototype, "hasUser", null);
tslib_1.__decorate([
    mobx_1.action
], UserService.prototype, "setUser", null);
class SSOUserService extends UserService {
    init() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (index_1.Services.SSO.isAuthenticated) {
                yield this.refresh();
            }
            else {
                // This reaction MAY be redundant as the login will never happen
                // within this page since we redirect to Auth0 for login, and redirect back
                // once login is complete.
                (0, mobx_1.reaction)(() => index_1.Services.SSO.user, () => tslib_1.__awaiter(this, void 0, void 0, function* () {
                    yield this.refresh();
                }));
            }
        });
    }
    login() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            yield index_1.Services.SSO.login();
        });
    }
    logout() {
        return tslib_1.__awaiter(this, arguments, void 0, function* (federated = false) {
            yield index_1.Services.SSO.logout(federated);
        });
    }
    reloadCurrentUser() {
        if (this.current != null) {
            this.load(this.current);
        }
    }
    load(user) {
        if (index_1.Services.SSO.isAuthenticated) {
            this.rest = index_1.Services.Rest.rest.bind(index_1.Services.SSO.token);
            try {
                index_1.Services.Department.rest.updateBoundUser(this.rest);
            }
            catch (err) {
                index_1.Logger.error({ error: err }, 'Failed updating bound user');
                notification_service_1.NotificationService.errorAsString('Fail to load user.');
            }
        }
        this.setUser(user);
        return this.current;
    }
    /**
     * Check to see if the current user has changed
     */
    refresh() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            var _a;
            if (index_1.Services.SSO.isAuthenticated) {
                index_1.Logger.info({
                    currentUser: this.current && this.current.username,
                    accessToken: index_1.Services.SSO.token,
                }, 'SSOUserProfile');
                const { user, token } = index_1.Services.SSO;
                try {
                    this.rest = yield index_1.Services.Rest.rest.authenticate(user.sub, token);
                }
                catch (e) {
                    if (core_shared_1.IkeRequestError.isIkeRequestError(e) && core_shared_1.IkeRequestErrorChecker.isValidationFailed(e)) {
                        index_1.Logger.error(e.message);
                        let msg = ((_a = e.errors[0]) === null || _a === void 0 ? void 0 : _a.message) || (0, i18n_1.Translate)(i18n_1.T.ERROR_GENERAL);
                        notification_service_1.NotificationService.errorAsString(msg);
                    }
                    else {
                        index_1.Services.Log.Logger.error(e);
                        index_1.Logger.error(i18n_1.T.ERROR_GENERAL);
                        notification_service_1.NotificationService.error({ key: i18n_1.T.ERROR_GENERAL });
                    }
                    //we have failed to authenticate user and thrown some notifications
                    //Clear out set info, logout, go back to login page.
                    this.rest = null;
                    if (this.hasUser) {
                        const userKey = index_1.Services.LocalStorage.storageKey(USER_STORAGE_KEY);
                        index_1.Services.LocalStorage.removeItem(userKey);
                    }
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    this.logout(true); //logout from SSO
                }
                if (this.rest && token) {
                    const profile = yield this.rest.User.me();
                    this.rest.me.init(profile);
                    this.setUser(profile);
                }
            }
        });
    }
    get isAuthenticated() {
        return index_1.Services.SSO.isAuthenticated;
    }
}
exports.SSOUserService = SSOUserService;
tslib_1.__decorate([
    mobx_1.action
], SSOUserService.prototype, "refresh", null);
tslib_1.__decorate([
    mobx_1.computed
], SSOUserService.prototype, "isAuthenticated", null);
class UserMutator {
    static setUnits(units) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            index_1.Services.Analytics.trackEvent(index_1.Services.Analytics.EventCategory.Mutate, 'User_setSettings.units', units, 1);
            const user = yield index_1.Services.User.rest.User.Settings.change({ units });
            return index_1.Services.User.load(user);
        });
    }
    static setDateFormat(dateFormat) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            index_1.Services.Analytics.trackEvent(index_1.Services.Analytics.EventCategory.Mutate, 'User_setSettings.dateFormat', dateFormat, 1);
            const user = yield index_1.Services.User.rest.User.Settings.change({ dateFormat });
            return index_1.Services.User.load(user);
        });
    }
    static setDepartment(departmentId) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            index_1.Services.Analytics.trackEvent(index_1.Services.Analytics.EventCategory.Mutate, 'User_setSettings.departmentId', departmentId, 1);
            const user = yield index_1.Services.User.rest.User.Settings.change({ departmentId });
            return index_1.Services.User.load(user);
        });
    }
    static requestPasswordReset(email) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            index_1.Services.Analytics.trackEvent(index_1.Services.Analytics.EventCategory.Mutate, 'User_requestPasswordReset', email, 1);
            try {
                yield index_1.Services.Rest.rest.SSOUser.triggerResetPassword({ email });
                return true;
            }
            catch (_e) {
                return false;
            }
        });
    }
}
exports.UserMutator = UserMutator;
//# sourceMappingURL=user.service.js.map